import notification from "antd/es/notification";
import { isNumber } from "lodash-es";
import { all, put, select, takeLatest } from "redux-saga/effects";

import DistanceUnit from "@mapmycustomers/shared/enum/DistanceUnit";
import EntityType from "@mapmycustomers/shared/enum/EntityType";
import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import OrganizationFeature from "@mapmycustomers/shared/enum/OrganizationFeature";
import OrganizationSetting from "@mapmycustomers/shared/enum/OrganizationSetting";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { EntityTypesSupportedByMapsPage, MapEntity } from "@mapmycustomers/shared/types/entity";
import { MapEntitiesSet, MapRecordsResponse, MultiPin } from "@mapmycustomers/shared/types/map";
import PinLegend from "@mapmycustomers/shared/types/map/PinLegend";
import { ColorPinLegend, ShapePinLegend } from "@mapmycustomers/shared/types/map/PinLegend";
import { MapPersistedRecordListConfiguration } from "@mapmycustomers/shared/types/map/types";
import Organization, { OrganizationMetaData } from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import RangeModel from "@mapmycustomers/shared/types/viewModel/internalModel/RangeModel";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";
import PlatformFilterModel, {
  PinLegendsFilter,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import PlatformSortModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformSortModel";

import {
  isAddressAreaSearchQuery,
  isCoordinatesAreaSearchQuery,
  isEntityAreaSearchQuery,
} from "@app/component/input/AreaSearchInput/utils/assert";
import getErrorNotificationDescription from "@app/component/map/core/utils/getErrorNotificationDescription";
import i18nService from "@app/config/I18nService";
import ReportType from "@app/enum/ReportType";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { MAX_ITEMS_TO_DOWNLOAD_FILE } from "@app/store/exportEntities/const";
import {
  getCurrentUser,
  getFeatures,
  getOrganizationId,
  getOrganizationSettingValue,
  getPosition,
} from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import { getAllColorLegends, getAllShapeLegends } from "@app/store/pinLegends";
import convertMapFilterModelToFilterModelForEntity from "@app/store/savedFilters/convertMapFilterModelToFilterModel";
import { AreaSearchQuery } from "@app/types/filters/AreaSearchQuery";
import GeoPath from "@app/types/GeoPath";
import Report from "@app/types/Report";
import { getLocalTimeZoneFormattedOffset } from "@app/util/dates";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { UniversalFieldName } from "@app/util/fieldModel/universalFieldsFieldModel";
import downloadEntitiesAsCsv from "@app/util/file/downloadEntitiesAsCsv";
import { formatDate } from "@app/util/formatters";
import buildPinLegends from "@app/util/map/buildPinLegends";
import { MAP_ENTITY_TYPES } from "@app/util/map/consts";
import { mapEntityIdParser } from "@app/util/map/idParsers";
import getColorShapeForEntity from "@app/util/map/markerStyles/getColorShapeForEntity";
import { getEntityTypeDisplayName } from "@app/util/ui";
import convertMapFilterToPlatformFilterModel from "@app/util/viewModel/convertMapFilterToPlatformFilterModel";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import {
  applyRouteRecordsListSettings,
  downloadRouteMultiPinRecords,
  fetchRouteMultiPinRecords,
  showRouteMultiPinRecords,
  updateRouteRecordsListCustomization,
} from "../../actions";
import { messages } from "../../messages";
import {
  getRouteLassoPaths,
  getRouteListViewLocationQuery,
  getRouteMultiPin,
  getRouteMultiPinRecordsRange,
  getRouteRecordsListSearch,
  getRouteRecordsListSortOrder,
  getRouteSelectedItems,
  getRouteTool,
  getRouteViewState,
} from "../../selectors";

export function* onShowRouteMultiPinRecords({
  payload,
}: ReturnType<typeof showRouteMultiPinRecords.request>) {
  try {
    yield put(fetchRouteMultiPinRecords.request({}));

    let address: string | undefined;
    if (isNumber(payload?.region?.longitude) && isNumber(payload?.region?.latitude)) {
      const orgId: Organization["id"] = yield select(getOrganizationId);
      const result: GeocodeResult = yield callApi("reverseGeocodeAddress", orgId, {
        coordinates: [payload.region!.longitude, payload.region!.latitude],
        type: "Point",
      });
      address = result.address.formattedAddress ?? "";
    }

    yield put(showRouteMultiPinRecords.success({ address }));
  } catch (error) {
    yield put(showRouteMultiPinRecords.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchRouteMultiPinRecords() {
  try {
    const viewState: MapViewState = yield select(getRouteViewState);
    const multiPin: MultiPin | undefined = yield select(getRouteMultiPin);
    const range: RangeModel = yield select(getRouteMultiPinRecordsRange);
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const colorPinLegends: ColorPinLegend[] = yield select(getAllColorLegends);
    const shapePinLegends: ShapePinLegend[] = yield select(getAllShapeLegends);
    const sort: SortModel = yield select(getRouteRecordsListSortOrder);
    const search: string | undefined = yield select(getRouteRecordsListSearch);

    if (!multiPin || viewState.visibleEntities.length === 0) {
      yield put(fetchRouteMultiPinRecords.success({ count: 0, records: [] }));
      return;
    }

    const mapFilters = convertMapFilterToPlatformFilterModel(
      viewState.filter,
      viewState.visibleEntities
    );

    const searchFilter = search?.trim();
    if (searchFilter?.length) {
      if (mapFilters.entities && !Array.isArray(mapFilters.entities)) {
        const entities = mapFilters.entities as Record<
          EntityTypesSupportedByMapsPage,
          PlatformFilterModel
        >;
        (Object.keys(entities) as EntityTypesSupportedByMapsPage[]).forEach((entityKey) => {
          if (!("$and" in entities[entityKey])) {
            entities[entityKey].$and = [];
          }
          const andCondition = entities[entityKey].$and;
          if (Array.isArray(andCondition)) {
            andCondition.push({ name: { $in: searchFilter } });
          }
        });
      }
    }

    let order: PlatformSortModel = undefined;
    if (sort?.length > 0) {
      const sortField = sort[0].field;
      const sortOrder = sort[0].order;

      if (sortField?.sortName === UniversalFieldName.GEOPOINT) {
        const unit: DistanceUnit =
          (yield select(getOrganizationSettingValue, OrganizationSetting.DISTANCE_UNIT)) ??
          DistanceUnit.MILE;
        const query: AreaSearchQuery | undefined = yield select(getRouteListViewLocationQuery);
        let coordinates: LongLat | undefined;
        if (query && (isCoordinatesAreaSearchQuery(query) || isAddressAreaSearchQuery(query))) {
          coordinates = query.coordinates;
        } else if (query && isEntityAreaSearchQuery(query) && query.entity.geoPoint?.coordinates) {
          coordinates = query.entity.geoPoint?.coordinates;
        } else {
          // Fall back to current user position if no anchor query is set yet.
          const position: GeolocationPosition | undefined = yield select(getPosition);
          if (
            position?.coords?.longitude !== undefined &&
            position?.coords?.latitude !== undefined
          ) {
            coordinates = [position.coords.longitude, position.coords.latitude];
          }
        }
        if (coordinates) {
          order = {
            order: sortOrder === SortOrder.DESC ? "desc" : "asc",
            point: coordinates,
            unit,
          };
        }
      } else {
        order = convertToPlatformSortModel(sort);
      }
    }

    const requestPayload = {
      $filters: {
        ...mapFilters,
        bounds: multiPin.bounds,
        cadence: true,
        includeAccessStatus: true,
        includeAssociations: true,
        includeCustomFields: true,
        includeEmailSubscriptionStatus: true,
        pinLegends: buildPinLegends(
          viewState.visibleEntities,
          viewState,
          colorPinLegends,
          shapePinLegends
        ),
      },
      $limit: range.endRow - range.startRow,
      $offset: range.startRow,
      $order: order,
    };

    const response: ListResponse<MapEntity> = yield callApi("fetchPins", orgId, requestPayload);

    yield put(
      fetchRouteMultiPinRecords.success({
        count: response.total,
        records: response.data,
      })
    );
  } catch (error) {
    yield put(fetchRouteMultiPinRecords.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onDownloadRouteMultiPinRecords({
  payload,
}: ReturnType<typeof downloadRouteMultiPinRecords>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const currentUser: User = yield select(getCurrentUser);
    const viewState: MapViewState = yield select(getRouteViewState);
    const multiPin: MultiPin | undefined = yield select(getRouteMultiPin);
    const activeTool: MapTool | undefined = yield select(getRouteTool);
    const lassoPaths: GeoPath[] = yield select(getRouteLassoPaths);
    const selectedItems: MapEntitiesSet = yield select(getRouteSelectedItems);
    const sort: SortModel = yield select(getRouteRecordsListSortOrder);
    const search: string | undefined = yield select(getRouteRecordsListSearch);
    const features: OrganizationMetaData["features"] = yield select(getFeatures);
    const disallowColor = features?.[OrganizationFeature.DISALLOW_COLOR]?.enabled;
    const colorPinLegends: PinLegend[] = yield select(getAllColorLegends);
    const shapePinLegends: PinLegend[] = yield select(getAllShapeLegends);
    const intl = i18nService.getIntl();

    const isLassoMode = activeTool === MapTool.LASSO && lassoPaths.length > 0;
    if (!multiPin && !isLassoMode) {
      return;
    }
    if (isLassoMode && selectedItems.size === 0) {
      return;
    }

    const visibleEntityTypes = viewState.visibleEntities;
    const mapFilters = convertMapFilterToPlatformFilterModel(viewState.filter, visibleEntityTypes);

    const entities = mapFilters.entities as Record<
      EntityTypesSupportedByMapsPage,
      PlatformFilterModel
    >;
    Object.keys(entities).forEach((entityKey) => {
      entities[entityKey as EntityTypesSupportedByMapsPage].includeGroups = true;
    });

    const searchFilter = search?.trim();
    if (searchFilter?.length) {
      (Object.keys(entities) as EntityTypesSupportedByMapsPage[]).forEach((entityKey) => {
        if (!("$and" in entities[entityKey])) {
          entities[entityKey].$and = [];
        }
        const andCondition = entities[entityKey].$and;
        if (Array.isArray(andCondition)) {
          andCondition.push({ name: { $in: searchFilter } });
        }
      });
    }

    if (isLassoMode) {
      const entityIdFilter: Record<EntityTypesSupportedByMapsPage, Array<MapEntity["id"]>> = {
        [EntityType.COMPANY]: [],
        [EntityType.DEAL]: [],
        [EntityType.PERSON]: [],
      };
      for (const mapEntityId of selectedItems.values()) {
        const parsed = mapEntityIdParser(mapEntityId);
        if (parsed) {
          entityIdFilter[parsed.entity as EntityTypesSupportedByMapsPage].push(parsed.id);
        }
      }
      (Object.keys(entities) as EntityTypesSupportedByMapsPage[]).forEach((entityKey) => {
        if (visibleEntityTypes.includes(entityKey)) {
          if (!("$and" in entities[entityKey])) {
            entities[entityKey].$and = [];
          }
          const andCondition = entities[entityKey].$and;
          if (Array.isArray(andCondition)) {
            andCondition.push({ id: { $in: entityIdFilter[entityKey] } });
          }
        }
      });
    }

    const bounds = isLassoMode ? undefined : multiPin?.bounds;

    const pinLegendFilter = disallowColor
      ? {
          pinLegends: MAP_ENTITY_TYPES.reduce(
            (result, entityType) => ({
              ...result,
              [entityType]: getColorShapeForEntity(
                viewState,
                colorPinLegends,
                shapePinLegends,
                entityType
              ),
            }),
            { $offset: getLocalTimeZoneFormattedOffset() } as PinLegendsFilter
          ),
        }
      : {};

    let order: PlatformSortModel = undefined;
    if (sort?.length > 0) {
      const sortField = sort[0].field;
      const sortOrder = sort[0].order;

      if (sortField?.sortName === UniversalFieldName.GEOPOINT) {
        const unit: DistanceUnit =
          (yield select(getOrganizationSettingValue, OrganizationSetting.DISTANCE_UNIT)) ??
          DistanceUnit.MILE;
        const position: GeolocationPosition | undefined = yield select(getPosition);
        if (
          unit &&
          position?.coords?.longitude !== undefined &&
          position?.coords?.latitude !== undefined
        ) {
          order = {
            order: sortOrder === SortOrder.DESC ? "desc" : "asc",
            point: [position.coords.longitude, position.coords.latitude],
            unit,
          };
        }
      } else {
        order = convertToPlatformSortModel(sort);
      }
    }

    let entitiesWithResponseLimitCount = 0;
    let downloadedEntities = 0;
    for (const entityType of visibleEntityTypes) {
      const requiredEntity = (
        mapFilters.entities as Partial<Record<EntityTypesSupportedByMapsPage, PlatformFilterModel>>
      )?.[entityType];
      const checkFilters = { ...mapFilters, entities: { [entityType]: requiredEntity } };
      const requestPayload = {
        $filters: {
          ...checkFilters,
          ...pinLegendFilter,
          bounds,
          cadence: true,
          includeCustomFields: true,
        },
        $limit: 0,
        $order: order,
      };
      const columns =
        payload
          .get(entityType)
          ?.filter(({ visible }) => visible)
          .map(({ field }) => field) ?? [];
      const response: MapRecordsResponse = yield callApi("fetchPins", orgId, requestPayload);
      if (response.total > MAX_ITEMS_TO_DOWNLOAD_FILE) {
        const $filters = convertToPlatformFilterModel(
          convertMapFilterModelToFilterModelForEntity(entityType, viewState.filter),
          getFieldModelByEntityType(entityType).fields.map((field) => ({ field, visible: true })),
          getFieldModelByEntityType(entityType)
        );
        const selectedColumns = columns.map(({ exportColumnName }) => exportColumnName);
        const reportPayload = {
          description: "",
          name: `${
            intl
              ? getEntityTypeDisplayName(intl, entityType, {
                  lowercase: false,
                  plural: false,
                })
              : entityType
          } ${formatDate(new Date(), "Pp")}`,
          selectedColumns,
          selectedFilters: {
            ...$filters,
            ...pinLegendFilter,
            bounds,
            cadence: true,
            includeCustomFields: true,
            includeEmailSubscriptionStatus: true,
          },
          tableName: entityType,
        };

        try {
          const report: Report = yield callApi("createReport", orgId, reportPayload);
          yield callApi("generateReport", orgId, report.id, currentUser.username);
          entitiesWithResponseLimitCount += 1;
        } catch {
          notification.error({
            description: getErrorNotificationDescription(intl, reportPayload.tableName),
            message: intl?.formatMessage(messages.downloadError),
          });
        }
      } else {
        const fullResponse: MapRecordsResponse = yield callApi("fetchPins", orgId, {
          ...requestPayload,
          $limit: MAX_ITEMS_TO_DOWNLOAD_FILE,
        });
        if (fullResponse.data.length > 0) {
          let reportEntityType;
          switch (entityType) {
            case EntityType.COMPANY:
              reportEntityType = ReportType.COMPANIES;
              break;
            case EntityType.DEAL:
              reportEntityType = ReportType.DEALS;
              break;
            case EntityType.PERSON:
              reportEntityType = ReportType.PEOPLE;
              break;
          }
          downloadEntitiesAsCsv(
            `${reportEntityType}_preview.csv`,
            entityType,
            fullResponse.data,
            columns
          );
          downloadedEntities += 1;
        }
      }
    }
    if (intl) {
      if (downloadedEntities > 0) {
        notification.success({
          message: intl.formatMessage(messages.downloadSuccess, {
            multiple: downloadedEntities > 1,
          }),
        });
      }
      if (entitiesWithResponseLimitCount > 0) {
        notification.warning({
          description: intl.formatMessage(messages.downloadDataFileTooLargeDescription),
          message: intl.formatMessage(messages.downloadDataFileTooLarge, {
            multiple: entitiesWithResponseLimitCount > 1,
          }),
        });
      }
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onUpdateRouteRecordsListCustomization({
  payload,
}: ReturnType<typeof updateRouteRecordsListCustomization.request>) {
  try {
    const currentSort: SortModel = yield select(getRouteRecordsListSortOrder);

    yield put(applyRouteRecordsListSettings(payload));

    const recordListConfig: MapPersistedRecordListConfiguration = {
      sort: {
        field: payload.sort?.[0]?.field.name ?? currentSort?.[0]?.field.name,
        order: payload.sort?.[0]?.order ?? currentSort?.[0]?.order,
      },
    };
    yield put(updateMetadata.request({ mapRecordsListSettings: recordListConfig }));
    yield put(updateRouteRecordsListCustomization.success());
  } catch (error) {
    yield put(updateRouteRecordsListCustomization.failure());
    yield put(handleError({ error }));
  }
}

export function* recordsSaga() {
  yield all([
    takeLatest(showRouteMultiPinRecords.request, onShowRouteMultiPinRecords),
    takeLatest(fetchRouteMultiPinRecords.request, onFetchRouteMultiPinRecords),
    takeLatest(downloadRouteMultiPinRecords, onDownloadRouteMultiPinRecords),
    takeLatest(updateRouteRecordsListCustomization.request, onUpdateRouteRecordsListCustomization),
  ]);
}
